import React, { useState, Fragment, useRef, useEffect } from "react";
import {
  Flex,
  Input,
  Button,
  Tabs,
  isNullish,
  Text,
  Menu,
  Alert,
  Icon,
  LabelWrapper,
  Divider,
  OverflowText,
  TextArea
} from "@theorgchartdev/storybook";
import cx from "classnames";
import { FieldInputValuePickerComposite } from "@/appComponents/Blocks/Composites/FieldInputValuePickerComposite/FieldInputValuePickerComposite";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { RecordDisplayLabel } from "@/appComponents/Blocks/Composites/RecordDisplayLabel/RecordDisplayLabel";
import { FormatValueFromFieldName, GetBoxPhoto } from "@/Utils/DataLib";
import {
  isBlockedField,
  getBlockedFieldDisplayValue,
  sanitizeBlockedValue,
  appendParenthesizedValue
} from "@coreapp/utils/FormatFieldValue";
import { GetChangeTypeIconProps } from "@/appComponents/Navigation/RightSidebar/Planning/Activity/Activity.utils";
import DateInputField from "@/appComponents/Blocks/Composites/ReactInputFields/DateInputField/DateInputField";
import { appstr } from "@coreapp/utils/appstr";
import {
  AssignmentStatus,
  enumCellObjectSubtypes,
  enumFeaturesList,
  enumObjectTypes,
  enumOverrideType,
  kAuxSuperID,
  kDivisionID,
  kEmployeeAssignmentStatusID,
  kEmployeeCostCenter,
  kEmployeePay,
  kEmployeePositionID,
  kEmployeeSalary,
  kNameID,
  kParentPositionID,
  kPersonID,
  kPhotoID,
  kPositionID,
  kSupervisorID,
  kTitleID,
  enumObjClassNames,
  kOrphanID
} from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import UpgradeTabHeader from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeTabHeader";
import { isValidDate } from "@coreapp/utils/isValidDate";
import { AdditionalReportsTab } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/DataSources/AppendTable/AdditionalReportsTab";
import { useAdditionalReports } from "@/hooks/useAdditionalReports";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";
import { useRecordEditorController } from "./useRecordEditorController";
import { DoValidationAlert } from "./RecordEditor.utils";
import {
  ProcessBasicField,
  ProcessAdvancedField,
  GetSuperIDName,
  GetSourceValueByField
} from "./RecordEditor.utils";
import { getFeatureAccess } from "@/Utils/Misc";
import { IsAppendTableContext } from "@coreapp/utils/IsAppendTableContext";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import BaseRecordEditorModal from "./BaseRecordEditorModal";
import { SelectableSearchMenu } from "@/appComponents/Blocks/Menus/BasicChartSearchMenu/SelectableSearchMenu";
import {
  AssignmentStatusPill,
  isAssignmentStatus
} from "@/appComponents/Blocks/Cards/ProfileBaseballCard/AssignmentStatusPill";
import { FormatPositionLabel } from "@coreapp/utils/FormatPositionLabel";
import { SupervisorFieldInput } from "./Components/BasicInfoSection";
import { BoxChip } from "@/Utils/BoxChip";
import { calculateModalSize } from "./ModalSizing.utils";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";

const isBudgetFieldCheck = fieldId => {
  const budgetFieldIds = [kEmployeePay, kEmployeeSalary, kEmployeeCostCenter];
  return budgetFieldIds.includes(fieldId);
};

const RecordEditorModal = ({
  show,
  overrideObj,
  dbObj,
  type,
  basicFields,
  additionalFields,
  onSave,
  onClose,
  data,
  cloneOverride,
  manager,
  showEffectiveDate,
  allowOrphans = false,
  loadRecord = true,
  branchAdd = false,
  initChanges = {},
  skipValidation,
  isPlanEdit,
  dataTestIdPrefix,
  initTab,
  additionalFieldsEmptyNode = null,
  additionalFieldsActionNode = null,
  otherHeaderProps,
  otherFooterProps,
  overlayProps,
  showCommentsTab = true,
  queryChartId = null,
  manualFields = [], // Fields from table's additionalFields that may not have fieldDefs
  ...props
}) => {
  const { initialWidth, initialHeight } = calculateModalSize();

  const [errorFields, setErrorFields] = useState([]);
  const [isValid, setIsValid] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const existingComment = overrideObj ? overrideObj.SmartGetFieldValue("Comment") : "";
  const { showPricingAndPackaging } = usePermissionCheck();

  // Effective Date - For Planning
  let changeEffDate = new Date();
  if (showEffectiveDate) {
    let change = overrideObj.lastActiveChange();
    if (change != null && change.fEffectiveDate != null) changeEffDate = change.fEffectiveDate;
  }
  const [effectiveDate, setEffectiveDate] = useState(changeEffDate);
  const [currentTab, setCurrentTab] = useState(initTab ?? 0);
  const [photoSrc, setPhotoSrc] = useState(null);

  const searchHandleRef = useRef(null);

  const doc = manager?.GetDoc() || GetDoc();
  const isPositionChart = doc.IsPositionDocument();

  const isPositionHolder = () => {
    return isPositionChart && dbObj?.InferObjectType() === enumObjectTypes.holder;
  };

  const hasBlockedValues = dbObj?.HasConditionallyBlockedFields() || false;
  const overrideHasConflicts = manager?.OverrideHasConflicts(overrideObj);
  const isEditable = !overrideHasConflicts;

  const { conflictContextValue } = usePlanManagerContext();
  const { setShowConflictModal, setConflictOverride } = conflictContextValue || {};

  const {
    record,
    baseRecord,
    recordSupervisor,
    baseRecordSupervisor,
    assignedPosition,
    baseAssignedPosition,
    override,
    existingSupervisorID,
    changeResults,
    changeType,
    madeFieldReverts,
    workingOverride,
    UpdateComment,
    comment,
    CommentOnlyChange,
    IsOnlyCommentChange,
    GetActiveChangeValueByField,
    lastActiveChangedField,
    GetCurrentFieldValue,
    updateRecordSupervisorAsDict,
    updateAssignedPositionAsDict,
    LoadAssignedPositionDetails,
    unsavedFieldChange,
    GetExistingFieldChanges,
    UpdateFieldValue,
    SaveRecordChanges,
    LoadSupervisorDetails,
    setOverride,
    setMadeFieldReverts,
    UnsavedChanges,
    changeTarget,
    InitializeTarget,
    DoRevertAll,
    updateRecordEditorState,
    IsAddOverride,
    GetOverrideStopIndex,
    GetBaseFieldValue
  } = useRecordEditorController({
    dbObj,
    overrideObj,
    type,
    manager,
    initChanges,
    isPlanEdit,
    allowOrphans,
    branchAdd
  });

  useEffect(() => {
    if (overrideObj && dbObj && !isInitialized) {
      InitializeTarget(overrideObj, dbObj, false);
      setIsInitialized(true);
    }
  }, [overrideObj, dbObj, isInitialized, InitializeTarget]);

  // Load supervisor details when component mounts if loadRecord is enabled
  useEffect(() => {
    if (loadRecord && overrideObj) {
      LoadSupervisorDetails(overrideObj);

      // For position charts with holder objects, also load assigned position
      if (isPositionHolder()) {
        const empPosFieldDef = FindFieldDef(kEmployeePositionID);
        const employeePositionId = GetCurrentFieldValue(empPosFieldDef.GetFieldName());
        if (employeePositionId) {
          LoadAssignedPositionDetails(employeePositionId);
        }
      }
    }
  }, []);

  // Prepare controller data to pass to BaseRecordEditorModal
  const controllerData = {
    record,
    baseRecord,
    recordSupervisor,
    override,
    existingSupervisorID,
    changeResults,
    changeType,
    madeFieldReverts,
    workingOverride,
    IsOnlyCommentChange,
    CommentOnlyChange,
    comment,
    UpdateComment
  };

  // Controller functions to pass to BaseRecordEditorModal
  const handleUnsavedChanges = () => UnsavedChanges();

  const handleRevertOverride = () => DoRevertAll();

  const handleInitializeTarget = (newOverrideObj, newDbObj, loadRecord) => {
    const result = changeTarget(() => {}, newOverrideObj, newDbObj, loadRecord);
    return result;
  };

  const handleLoadRecordFallback = (recordID, isPositionObj) => {
    const setRecordFallbackCallback = r => {
      let resObject = typeof r === "string" ? JSON.parse(r) : r;
      if (!resObject.data || !resObject.data[0]) return;
      updateRecordEditorState({ fetchedRecordFallback: resObject.data[0] ?? {} });
    };

    const loadParams = { recordIDs: [recordID] };
    if (isPositionObj) {
      loadParams.includeHolders = true;
    }

    manager?.LoadRecords?.(loadParams, r => setRecordFallbackCallback(r), isPositionObj);
  };

  const additionalReportsHook = props.showAdditionalReportsTab
    ? useAdditionalReports(props.chartConfig, record, (fieldName, getPreviousValue) =>
        GetActiveChangeValueByField(fieldName, getPreviousValue, {
          checkFieldDef: false,
          checkHeaders: true
        })
      )
    : {
        reportsData: [],
        validation: {},
        auxSuperFields: [],
        handleAddAuxSupervisor: () => {},
        handleSearchSelect: () => {},
        handleClearReport: () => {},
        handleRemove: () => {},
        isValidReports: () => true,
        getReportChanges: () => ({ AuxChanges: {}, auxMappingChanges: { added: [], removed: [] } }),
        getChartConfigChanges: () => null,
        getTableFieldChanges: () => ({})
      };

  const {
    reportsData,
    validation,
    auxSuperFields,
    handleAddAuxSupervisor,
    handleSearchSelect,
    handleClearReport,
    handleRemove,
    isValidReports,
    getReportChanges,
    getChartConfigChanges,
    getTableFieldChanges
  } = additionalReportsHook;

  const validateFields = mergedChangeResults => {
    let failureArr = [];

    // Validate reports tab if enabled
    if (props?.showAdditionalReportsTab && !isValidReports()) {
      failureArr.push(appstr("Invalid reporting structure"));
    }

    return failureArr;
  };

  // Function to handle save and include additional report changes
  const handleSave = async (validate = true) => {
    // First check if reports are valid if that tab is shown
    if (validate && props?.showAdditionalReportsTab && !isValidReports()) {
      DoValidationAlert([appstr("Invalid reporting structure")]);
      return { success: false };
    }

    // Get additional changes from the reports
    const { AuxChanges } = getReportChanges();

    // Get any comment from the BaseRecordEditorModal
    // Note: We don't have direct access to the comment in the modal,
    // but the controller will add it from the BaseRecordEditorModal's call

    // Additional data for the save
    const additionalData = {
      effectiveDate
    };

    // if employee positionid field changed, we need to change override type to move
    const empPosFieldDef = FindFieldDef(kEmployeePositionID);
    if (isPositionHolder() && empPosFieldDef && unsavedFieldChange(empPosFieldDef.GetFieldName())) {
      additionalData.changeType = enumOverrideType.move;
    }

    // Use the centralized save routine from the controller
    const result = await SaveRecordChanges(
      handleSaveWithEffectiveDate, // This gets called with the merged result
      additionalData,
      validate,
      skipValidation,
      AuxChanges
    );

    if (!result.success && result.errors) {
      DoValidationAlert(result.errors);
      setErrorFields(result.errors);
      setIsValid(false);
      return;
    }

    setErrorFields([]);
    setIsValid(true);

    return result;
  };

  const handleSaveWithEffectiveDate = async ({ result, override, changeType }) => {
    // Important: This function gets called by the controller's SaveRecordChanges
    // The result already contains the merged changes including AuxChanges

    // Add effective date to result
    if (result) {
      result.effectiveDate = effectiveDate;
    }

    // Get mapping changes info
    const { auxMappingChanges } = getReportChanges();

    // Check if mapping change so we can alert the user
    if (auxMappingChanges.added.length > 0) {
      const { message, alertProps } = createBoldToastMessage("Update Base Chart Mapping (info)", {
        mappingPath: appstr("mappingPath"),
        AuxRepMapping: appstr("AuxRepMapping")
      });

      const wsMgr = GetWSMgr();
      wsMgr.CreateReactAlert(
        appstr("Update Base Chart Mapping"),
        appstr("Update Base Chart Mapping (long)"),
        () => {
          // Call the original onSave with all the needed data
          onSave({
            result,
            override,
            changeType,
            chartConfig: getChartConfigChanges(),
            ...getTableFieldChanges()
          });
        },
        () => {},
        appstr("Yes"),
        appstr("Cancel"),
        null,
        { alertProps: { ...alertProps, severity: "info", message } }
      );
      return;
    }

    // Regular save without mapping changes
    await onSave({
      result,
      override,
      changeType,
      chartConfig: null,
      ...getTableFieldChanges()
    });
  };

  const handleTabChange = currentTabIndex => {
    setCurrentTab(currentTabIndex);
  };

  const FindFieldDef = fieldID => {
    if (dbObj == null) return null;
    var objClass = dbObj.GetParentClass();
    var fieldDef = objClass.GetFieldDefByID(fieldID);
    if (!fieldDef) fieldDef = objClass.GetFieldDefByName(fieldID);
    return fieldDef;
  };

  const IsOrphaned = () => {
    const orphanFieldDef = FindFieldDef("Orphan");
    const orphanFieldName = orphanFieldDef.GetFieldName();
    const isOrphan = GetSourceValueByField(record, orphanFieldName);
    if (isOrphan === false) {
      return false;
    }

    const superKey = GetSuperIDName(dbObj);
    const changedSupervisor = GetCurrentFieldValue(superKey);
    if (!isNullish(changedSupervisor) && changedSupervisor.length > 0) {
      return false;
    }

    return true;
  };

  const CanRevertField = fieldDef => {
    const isPickList = fieldDef.GetFieldSubtype() === enumCellObjectSubtypes.picklist;
    return !IsOnlyCommentChange() && !isPickList;
  };

  const ShowFieldHistory = fieldDef => {
    const fieldChanges = GetExistingFieldChanges(fieldDef?.GetFieldName?.()) ?? [];
    if (!CanRevertField(fieldDef)) return false;
    return fieldChanges.length > 0;
  };

  const ShowSingleRevertAll = fieldDef => {
    const canRevert = ShowFieldHistory(fieldDef);
    const fieldChanges = GetExistingFieldChanges(fieldDef?.GetFieldName?.()) ?? [];
    return canRevert && fieldChanges.length <= 1;
  };

  const RevertFieldToSource = (fieldDef, alert = true) => {
    const fieldName = fieldDef?.GetFieldName();
    if (!fieldName) return;
    if (alert) {
      const wsMgr = GetWSMgr();
      wsMgr.CreateReactAlert(
        appstr("Revert all changes to") + " '" + fieldName + "'?",
        appstr("Confirm field revert"),
        () => {
          RevertFieldToSource(fieldDef, false);
        },
        () => {},
        appstr("Revert"),
        appstr("Cancel")
      );
      return;
    }
    // Reverting the supervisor of a plan move means reverting the whole move, so the plan
    // editor routes it through DoRevertAll. For the append table that same call is
    // "removeappendentry" — it deletes the entire override — so fall through to the
    // per-field delta strip below instead.
    if (fieldName === GetSuperIDName(dbObj) && !IsAppendTableContext(manager)) {
      DoRevertAll();
      onClose();
      return;
    }

    if (workingOverride?.fChanges) {
      const stopIndex = GetOverrideStopIndex();
      for (var j = workingOverride.fChanges.length - 1; j >= stopIndex; j--) {
        const changeDelta = workingOverride.fChanges[j]?.fDelta;
        if (!changeDelta) continue;
        const changedFields = Object.keys(changeDelta);
        if (changedFields.length === 1 && changedFields[0] === fieldName) {
          workingOverride.fChanges.splice(j, 1); // delete entire change
        } else {
          delete workingOverride.fChanges[j].fDelta[fieldName]; // delete from delta
        }
      }
    }

    setOverride({ state: workingOverride });
    setMadeFieldReverts(true);
    // Remove any staged (changeResults) value for the reverted field so it doesn't
    // override the correct source value in GetCurrentFieldValue.
    const { [fieldName]: _removed, ...remainingChangeResults } = changeResults;
    updateRecordEditorState({ changeResults: remainingChangeResults });

    // The Reports to chip renders from recordSupervisor state rather than the delta, so
    // stripping the delta alone leaves it showing the overridden supervisor. Re-resolve it
    // from the now-reverted value (workingOverride is mutated in place above, so the reads
    // below already see the stripped delta).
    if (fieldName === GetSuperIDName(dbObj)) {
      const revertedSuperID = GetActiveChangeValueByField(fieldName);
      if (revertedSuperID) LoadSupervisorDetails(workingOverride, revertedSuperID);
      else updateRecordSupervisorAsDict(null);

      // On position charts GetSuperIDName resolves to the assigned-position field, whose
      // label renders from assignedPosition state. LoadAssignedPositionDetails clears it
      // when the reverted value is blank.
      const empPosFieldName = FindFieldDef(kEmployeePositionID)?.GetFieldName();
      if (empPosFieldName && empPosFieldName === fieldName) {
        LoadAssignedPositionDetails(revertedSuperID);
      }
    }
  };

  const RevertFieldToPrior = o => {
    const fieldName = o.field;
    const changeIdx = o.value;
    let { index } = lastActiveChangedField(o.field, false, true);
    workingOverride.fChanges[index].fDelta[fieldName] =
      workingOverride.fChanges[changeIdx].fDelta[fieldName];
    setOverride({ state: workingOverride });
    setMadeFieldReverts(true);
  };

  const BuildPriorChangeValueOptions = fieldName => {
    let options = [];
    const changeList = workingOverride.fChanges;
    let skipFirst = false;
    const fieldDef = FindFieldDef(fieldName);
    const stopIndex = GetOverrideStopIndex();

    for (let index = changeList.length - 1; index >= stopIndex; index--) {
      const nextChange = changeList[index];
      if (Object.keys(nextChange.fDelta).includes(fieldName)) {
        if (!skipFirst) {
          skipFirst = true;
          continue;
        }
        const nextValue = nextChange.fDelta[fieldName];
        const nextValue_formatted = FormatValueFromFieldName(fieldName, nextValue);

        let option = {
          value: index,
          label: nextValue_formatted,
          clickHandler: RevertFieldToPrior,
          field: fieldName,
          divider: index === stopIndex
        };
        options.push(option);
      }
    }

    const rawBaseValue = GetBaseFieldValue(fieldDef);
    const formattedBaseValue = FormatValueFromFieldName(fieldName, rawBaseValue);
    // "" is as much "no source value" as null is, so don't let it through as an empty label.
    const formattedValue =
      formattedBaseValue == null || formattedBaseValue === ""
        ? appstr("Blank")
        : formattedBaseValue;

    let revertToSource = {
      clickHandler: () => RevertFieldToSource(fieldDef),
      revertAll: true,
      label: appendParenthesizedValue(appstr("Revert to source"), formattedValue)
    };
    options.push(revertToSource);

    return options;
  };

  // Get Revert Button Component
  const GetRevertButton = fieldDef => {
    if (!ShowFieldHistory(fieldDef)) return null;

    let originalValue = FormatValueFromFieldName(
      fieldDef.GetFieldName(),
      GetBaseFieldValue(fieldDef)
    );

    // Handle supervisor field specially
    const classDef = dbObj.GetParentClass();
    const superKeyDef = classDef?.GetSupervisorIDFieldDef();
    if (fieldDef.GetFieldID() === superKeyDef?.GetFieldID() && baseRecordSupervisor) {
      // For supervisor fields, get the name to display in the tooltip
      originalValue = sanitizeBlockedValue(baseRecordSupervisor?.SmartGetFieldValue(kNameID));

      // Fallback to ID if name not available
      if (!originalValue) {
        originalValue =
          baseRecordSupervisor?.SmartGetFieldValue(kPersonID) ||
          baseRecordSupervisor?.SmartGetFieldValue(kPositionID);
      }

      // For plan edits specifically, ensure we have the proper supervisor name format
      if (isPlanEdit && baseRecordSupervisor) {
        // Use baseRecordSupervisor's FormatEmployeeLabel method if available
        if (typeof baseRecordSupervisor.FormatEmployeeLabel === "function") {
          originalValue = baseRecordSupervisor.FormatEmployeeLabel();
        }
      }
    }

    // Handle Employee PositionID field for position charts
    if (fieldDef.GetFieldID() === kEmployeePositionID && isPositionChart && baseAssignedPosition) {
      originalValue = FormatPositionLabel(baseAssignedPosition);
    }

    const showSingleClickRevert = ShowSingleRevertAll(fieldDef);
    // Always name a value, matching usePositionRecall.GetRevertButton. A bare "Revert to
    // source" is ambiguous - it reads as the tooltip failing to resolve rather than the
    // source genuinely being empty.
    const hasOriginalValue = originalValue != null && originalValue !== "";
    const singleClickRevertTip = appendParenthesizedValue(
      appstr("Revert to source"),
      hasOriginalValue ? originalValue : appstr("Blank")
    );
    const buttonId = showSingleClickRevert ? "plan-single-revert-" : "plan-multi-revert-";

    const trigger = (
      <Button
        dataTestId={buttonId + fieldDef.GetFieldID()}
        modifier={"secondary"}
        iconOnly
        iconProps={{ name: showSingleClickRevert ? "rotate-left" : "clock-rotate-left" }}
        className={cx("oc-special-secondary", "oc-special-secondary--icon-only")}
        tooltipProps={{
          message: showSingleClickRevert ? singleClickRevertTip : appstr("Revert to prior value")
        }}
        onClick={showSingleClickRevert ? () => RevertFieldToSource(fieldDef) : undefined}
      />
    );

    if (showSingleClickRevert) return trigger;

    return (
      <Menu
        trigger={trigger}
        options={BuildPriorChangeValueOptions(fieldDef.GetFieldName())}
        listHeader={{ label: appstr("Field change history") }}
        value={"none"}
        listWidth={"medium"}
        optionSize={"small"}
        showCheck={false}
      />
    );
  };

  // Get Record Label Info
  const GetRecordLabelInfo = () => {
    let recordInfo = {};
    let recordExists = !isNullish(record);
    const recordIdField = dbObj.GetRecordIDFieldDef()?.GetFieldID();
    let recordDisplayFields = [kNameID, kTitleID, recordIdField, kDivisionID, kPhotoID];
    for (let field of recordDisplayFields) {
      let fieldDef = FindFieldDef(field);
      if (!fieldDef) continue;
      let value = record?.GetFieldValue(fieldDef);
      if (isBlockedField(value)) value = getBlockedFieldDisplayValue();

      if (!value) {
        value = GetActiveChangeValueByField(fieldDef.GetFieldName());
      }
      if (recordExists && field === kPhotoID && isNullish(photoSrc)) {
        let image = GetBoxPhoto(fieldDef, record);
        if (!isNullish(image)) {
          value = image.src;
          setPhotoSrc(value);
        }
      }
      recordInfo = { ...recordInfo, [field]: value };
    }
    return recordInfo;
  };

  const GetAssignmentStatus = dataObj => {
    const status = dataObj?.SmartGetFieldValue(kEmployeeAssignmentStatusID);

    if (!status || !isAssignmentStatus(status)) {
      return null;
    }
    return status;
  };

  const recordInfo = GetRecordLabelInfo();
  const assignmentStatus = GetAssignmentStatus(dbObj, record);

  // Build Basic Info Tab
  const superName = GetSuperIDName(dbObj);
  let superChanged =
    GetExistingFieldChanges(superName)?.length > 0 || unsavedFieldChange(superName);

  const contentPadding = "20px 24px";
  const { enableTE: fieldEnableTE } = ProcessBasicField(
    workingOverride,
    isPositionChart ? kEmployeePositionID : kSupervisorID,
    changeType ?? workingOverride?.SmartGetFieldValue("Type"),
    manager.GetManagerName(),
    IsOnlyCommentChange(),
    assignmentStatus
  );
  const enableTE = fieldEnableTE && isEditable;

  const handleChangeReportsTo = option => {
    UpdateFieldValue({ field: GetSuperIDName(dbObj), value: option.value });
    updateRecordSupervisorAsDict(option.result);
  };

  const handleRemoveReportsTo = () => {
    if (!allowOrphans) {
      return;
    }

    UpdateFieldValue({ field: GetSuperIDName(dbObj), value: "" });
    updateRecordSupervisorAsDict(null);
  };

  const parentClass = dbObj.GetParentClass();
  const recordIdField = parentClass.GetRecordIDFieldDef()?.GetFieldID();

  let mappedReportsTo = null;
  if (recordSupervisor) {
    mappedReportsTo = {
      id: recordSupervisor.SmartGetFieldValue(recordIdField),
      label: isPositionChart
        ? FormatPositionLabel(recordSupervisor)
        : (sanitizeBlockedValue(recordSupervisor.SmartGetFieldValue(kNameID)) ?? appstr("Blank")),
      value: recordSupervisor.SmartGetFieldValue(recordIdField),
      // Keep a reference to the original employee object for removal
      originalEmployee: recordSupervisor
    };
  }

  const getAssignedPositionSupervisorInfo = () => {
    if (!assignedPosition) return null;
    const positionSupervisorId = assignedPosition.SmartGetFieldValue(kParentPositionID);
    if (!positionSupervisorId) return null;

    const doc = manager?.GetDoc() || GetDoc();
    const objDB = doc?.GetObjDB();
    const positionClass = objDB?.GetClassByName(enumObjClassNames.position);
    const parentPosition = positionClass?.FindObject(
      positionClass?.GetFieldDefByID(kPositionID),
      positionSupervisorId,
      false
    );

    if (parentPosition) {
      try {
        const chipObject = BoxChip.FromObjDBObject(parentPosition);
        if (chipObject && typeof chipObject.label === "function") {
          return chipObject.label();
        }
      } catch (error) {
        return FormatPositionLabel(parentPosition);
      }
      return FormatPositionLabel(parentPosition);
    }
    return positionSupervisorId;
  };

  const getAlertInfo = () => {
    const outOfScope = assignmentStatus && assignmentStatus === AssignmentStatus.AssignedOutOfScope;
    if (outOfScope) {
      return { message: appstr("Record out of scope info"), status: "info" };
    }
    if (IsOrphaned()) {
      return { message: appstr("Record is disconnected from hierarchy"), status: "warning" };
    }
    return { message: null, status: null };
  };

  const alertInfo = getAlertInfo();

  const getBlockedFieldAlert = () => {
    if (!hasBlockedValues) return null;

    return (
      <Alert severity={"info"} showIcon className={cx("oc-padding-small", "oc-text-xsmall")}>
        {appstr("recordeditorcompaccesstip")}
      </Alert>
    );
  };

  let basicInfoTab = {};
  basicInfoTab.dataTestId = `${dataTestIdPrefix}tab-basic-info`;
  basicInfoTab.header = appstr("Basic info");
  basicInfoTab.content = (
    <Flex
      key={0}
      className={"ocn-list-scrollbar"}
      direction={"column"}
      padding={contentPadding}
      gap={"16px"}
      height={"100%"}
      overflow={"auto"}
    >
      <Flex direction={"column"} gap={"12px"}>
        {getBlockedFieldAlert()}
        {alertInfo?.message && (
          <Alert
            data-testid={`${dataTestIdPrefix}disconnected-record-alert`}
            className={cx("oc-padding-small", "oc-text-small")}
            severity={alertInfo?.status ?? "info"}
            withBorder
            showIcon
          >
            {alertInfo.message}
          </Alert>
        )}
        {/* For position holder edit, show assigned position first */}
        {isPositionHolder() ? (
          <>
            {basicFields.includes(kEmployeePositionID) &&
              (() => {
                const fieldDef = FindFieldDef(kEmployeePositionID);
                if (!fieldDef) return null;
                const fieldName = fieldDef.GetFieldName();
                const fieldChanged =
                  GetExistingFieldChanges(fieldName)?.length > 0 || unsavedFieldChange(fieldName);
                const value = GetCurrentFieldValue(fieldName);
                const assignedPositionSupervisorInfo = getAssignedPositionSupervisorInfo();

                return (
                  <Flex direction="column" gap="4px">
                    <Flex
                      gap={"8px"}
                      className={fieldChanged ? "oc-special-search" : null}
                      alignItems={"flex-end"}
                    >
                      <div style={{ flexGrow: 1 }}>
                        <SupervisorFieldInput
                          dataTestIdPrefix={dataTestIdPrefix}
                          label={
                            assignedPosition ? FormatPositionLabel(assignedPosition) : undefined
                          }
                          field={{
                            name: fieldName,
                            value: value,
                            textEditOptions: {
                              enableTE: isEditable && enableTE,
                              blankMsg: "<Blank>"
                            },
                            fieldDef: fieldDef,
                            error: false
                          }}
                          searchHandleRef={searchHandleRef}
                          fullWidth
                          clickHandler={option => {
                            UpdateFieldValue([
                              { field: fieldName, value: option.value },
                              {
                                field: kEmployeeAssignmentStatusID,
                                value: AssignmentStatus.Assigned
                              },
                              { field: kOrphanID, value: 0 }
                            ]);
                            if (option.result) {
                              updateAssignedPositionAsDict(option.result);
                            } else if (option.value) {
                              LoadAssignedPositionDetails(option.value);
                            }
                          }}
                          labelWrapperProps={{
                            size: "small",
                            require: false,
                            label: appstr("Assigned position")
                          }}
                          selectableSearchMenuProps={{
                            inputSize: "medium",
                            pillSize: "medium",
                            displayRecents: true,
                            expandable: false,
                            allowMultiSelect: false,
                            fullWidth: true,
                            queryChartId: queryChartId,
                            onRemoveItem:
                              isEditable && enableTE
                                ? () => {
                                    UpdateFieldValue([
                                      { field: fieldName, value: "" },
                                      {
                                        field: kEmployeeAssignmentStatusID,
                                        value: AssignmentStatus.Unassigned
                                      },
                                      { field: kOrphanID, value: 1 }
                                    ]);
                                    updateAssignedPositionAsDict(null);
                                  }
                                : undefined,
                            queryOptions: {
                              includeAux: false,
                              queryType: "position",
                              outsidePlan: changeType === enumOverrideType.moveout
                            },
                            handlePositions: true,
                            mockDoc: doc
                          }}
                        />
                      </div>
                      {GetRevertButton(fieldDef)}
                    </Flex>
                    {/* Reports to info text showing assigned position's supervisor */}
                    {assignedPositionSupervisorInfo && (
                      <OverflowText size="small" colorModifier="grey">
                        {appstr("Reports to")}: {assignedPositionSupervisorInfo}
                      </OverflowText>
                    )}
                  </Flex>
                );
              })()}
          </>
        ) : (
          <Flex direction="column" gap="4px">
            <Flex
              gap={"8px"}
              className={superChanged ? "oc-special-search" : null}
              width={"100%"}
              alignItems={"flex-end"}
            >
              <div style={{ flexGrow: 1 }}>
                <LabelWrapper label={appstr("Reports to")} compact>
                  <SelectableSearchMenu
                    dataTestId={`${dataTestIdPrefix}supervisor-input`}
                    listWidth={"fill"}
                    fullWidth
                    inputSize={"medium"}
                    pillSize={"medium"}
                    displayRecents={true}
                    selectedItem={mappedReportsTo}
                    onRemoveItem={
                      allowOrphans && isEditable && !(!enableTE || isPositionChart)
                        ? handleRemoveReportsTo
                        : undefined
                    }
                    expandable={false}
                    allowMultiSelect={false}
                    searchMenuProps={{
                      clearOnSelect: true,
                      clearable: true,
                      placeholder: appstr("Search for an employee"),
                      expandWidth: true,
                      searchProps: {
                        handleRef: searchHandleRef
                      }
                    }}
                    inputProps={{
                      autoFocus: false,
                      leftIcon: false,
                      disabled: !enableTE || isPositionChart
                    }}
                    handleSearchOptionClick={handleChangeReportsTo}
                    queryOptions={{
                      includeAux: false,
                      queryType: "person",
                      outsidePlan: changeType === enumOverrideType.moveout
                    }}
                    handlePositions={false}
                    mockDoc={doc}
                    queryChartId={queryChartId}
                  />
                </LabelWrapper>
              </div>
              {GetRevertButton(FindFieldDef(GetSuperIDName(dbObj)))}
              {allowOrphans && (
                <Button
                  dataTestId={`${dataTestIdPrefix}clear-supervisor-button`}
                  disabled={!enableTE || !recordSupervisor}
                  onClick={() => {
                    UpdateFieldValue({ field: GetSuperIDName(dbObj), value: "" });
                    updateRecordSupervisorAsDict(null);
                  }}
                  iconOnly
                  modifier={"secondary"}
                  iconProps={{ name: "xmark" }}
                  tooltipProps={{ message: appstr("clearsearch") }}
                  className={cx("oc-special-secondary", "oc-special-secondary--icon-only")}
                />
              )}
            </Flex>
          </Flex>
        )}
      </Flex>
      {isPositionHolder() ? (
        <>
          {/* Non-budget basic fields */}
          <Flex direction={"column"} className={cx("oc-grid", "oc-grid-narrow")} gap={"16px"}>
            {basicFields
              .filter(fieldId => !isBudgetFieldCheck(fieldId) && fieldId !== kEmployeePositionID)
              .map((fieldId, index) => {
                const {
                  skipTe,
                  blankMsg,
                  enableTE: fieldEnableTE
                } = ProcessBasicField(
                  workingOverride,
                  fieldId,
                  changeType ?? workingOverride?.SmartGetFieldValue("Type"),
                  manager.GetManagerName(),
                  IsOnlyCommentChange(),
                  assignmentStatus
                );
                const finalEnableTE = fieldEnableTE && isEditable;
                let fieldDef = FindFieldDef(fieldId);
                if (!fieldDef || skipTe) return null;
                let fieldName = fieldDef.GetFieldName();
                let fieldChanged =
                  GetExistingFieldChanges(fieldName)?.length > 0 || unsavedFieldChange(fieldName);
                let value = GetCurrentFieldValue(fieldName);
                const fieldHasError = errorFields.includes(fieldName);

                return (
                  <FieldInputValuePickerComposite
                    key={index}
                    field={fieldName}
                    value={value}
                    onChange={UpdateFieldValue}
                    dataTestIdPrefix={dataTestIdPrefix}
                    inputProps={{
                      status: fieldChanged ? "warning" : fieldHasError ? "error" : null,
                      message: "",
                      placeholder: blankMsg || "<Blank>",
                      disabled: !finalEnableTE,
                      size: "medium"
                    }}
                    triggerProps={{ disabled: !finalEnableTE, triggerModifier: "medium" }}
                    showTrigger={isEditable}
                    parentFieldDef={fieldDef}
                    showPickListWarning={true}
                    queryChartId={queryChartId}
                  >
                    {isEditable && GetRevertButton(fieldDef)}
                  </FieldInputValuePickerComposite>
                );
              })}
          </Flex>

          {/* Divider between basic info and budget fields */}
          <Divider />

          {/* Budget fields in their own grid */}
          <Flex direction={"column"} className={cx("oc-grid", "oc-grid-narrow")} gap={"16px"}>
            {basicFields
              .filter(fieldId => isBudgetFieldCheck(fieldId))
              .map((fieldId, index) => {
                const {
                  skipTe,
                  blankMsg,
                  enableTE: fieldEnableTE
                } = ProcessBasicField(
                  workingOverride,
                  fieldId,
                  changeType ?? workingOverride?.SmartGetFieldValue("Type"),
                  manager.GetManagerName(),
                  IsOnlyCommentChange(),
                  assignmentStatus
                );
                const finalEnableTE = fieldEnableTE && isEditable;
                let fieldDef = FindFieldDef(fieldId);
                if (!fieldDef || skipTe) return null;
                let fieldName = fieldDef.GetFieldName();
                let fieldChanged =
                  GetExistingFieldChanges(fieldName)?.length > 0 || unsavedFieldChange(fieldName);
                let value = GetCurrentFieldValue(fieldName);
                const fieldHasError = errorFields.includes(fieldName);

                return (
                  <FieldInputValuePickerComposite
                    key={`budget-${index}`}
                    field={fieldName}
                    value={value}
                    onChange={UpdateFieldValue}
                    dataTestIdPrefix={dataTestIdPrefix}
                    inputProps={{
                      status: fieldChanged ? "warning" : fieldHasError ? "error" : null,
                      message: "",
                      placeholder: blankMsg || "<Blank>",
                      disabled: !finalEnableTE,
                      size: "medium"
                    }}
                    triggerProps={{ disabled: !finalEnableTE, triggerModifier: "medium" }}
                    showTrigger={isEditable}
                    parentFieldDef={fieldDef}
                    showPickListWarning={true}
                    queryChartId={queryChartId}
                  >
                    {isEditable && GetRevertButton(fieldDef)}
                  </FieldInputValuePickerComposite>
                );
              })}
          </Flex>
        </>
      ) : (
        <Flex direction={"column"} className={cx("oc-grid", "oc-grid-narrow")} gap={"16px"}>
          {basicFields
            .filter(fieldId => fieldId !== kSupervisorID)
            .map((fieldId, index) => {
              const {
                skipTe,
                blankMsg,
                enableTE: fieldEnableTE
              } = ProcessBasicField(
                workingOverride,
                fieldId,
                changeType ?? workingOverride?.SmartGetFieldValue("Type"),
                manager.GetManagerName(),
                IsOnlyCommentChange(),
                assignmentStatus
              );
              const finalEnableTE = fieldEnableTE && isEditable;
              let fieldDef = FindFieldDef(fieldId);
              if (!fieldDef || skipTe) return null;
              let fieldName = fieldDef.GetFieldName();
              let fieldChanged =
                GetExistingFieldChanges(fieldName)?.length > 0 || unsavedFieldChange(fieldName);
              let value = GetCurrentFieldValue(fieldName);
              const fieldHasError = errorFields.includes(fieldName);

              return (
                <FieldInputValuePickerComposite
                  key={index}
                  field={fieldName}
                  value={value}
                  onChange={UpdateFieldValue}
                  dataTestIdPrefix={dataTestIdPrefix}
                  inputProps={{
                    status: fieldChanged ? "warning" : fieldHasError ? "error" : null,
                    message: "",
                    placeholder: blankMsg || "<Blank>",
                    disabled: !finalEnableTE,
                    size: "medium",
                    className: "ocn-plan-explorer-create-plan"
                  }}
                  triggerProps={{ disabled: !finalEnableTE, triggerModifier: "medium" }}
                  showTrigger={isEditable}
                  parentFieldDef={fieldDef}
                  showPickListWarning={true}
                  queryChartId={queryChartId}
                >
                  {isEditable && GetRevertButton(fieldDef)}
                </FieldInputValuePickerComposite>
              );
            })}
        </Flex>
      )}
    </Flex>
  );

  let header = appstr("Additional reports");
  if (showPricingAndPackaging && !getFeatureAccess(enumFeaturesList.auxMapping)) {
    header = (
      <UpgradeTabHeader
        label={appstr("Additional reports")}
        dataTestIdPrefix={`${dataTestIdPrefix}tab-additional-reports`}
      />
    );
  }

  let additionalReportsTab = {};
  if (props?.showAdditionalReportsTab) {
    additionalReportsTab.dataTestId = `${dataTestIdPrefix}tab-additional-reports`;
    additionalReportsTab.header = header;
    additionalReportsTab.content = (
      <div className={"oc-table-tab"}>
        <AdditionalReportsTab
          reportsData={reportsData}
          auxSuperFields={auxSuperFields}
          validation={validation}
          chartConfig={props?.chartConfig}
          handleAddAuxSupervisor={handleAddAuxSupervisor}
          handleSearchSelect={handleSearchSelect}
          handleClearReport={handleClearReport}
          handleRemove={handleRemove}
        />
      </div>
    );
  }

  // Build Additional Info Tab
  let additionalInfoTab = {};
  additionalInfoTab.dataTestId = `${dataTestIdPrefix}tab-additional-info`;
  additionalInfoTab.header = appstr("Additional info");
  additionalInfoTab.content = (
    <div className={cx("ocn-chart-explorer-list-container", "ocn-list-scrollbar", "oc-grid-wide")}>
      <Flex
        direction={"column"}
        height={"100%"}
        justifyContent={"space-between"}
        padding={contentPadding}
        gap={"12px"}
      >
        {getBlockedFieldAlert()}
        {additionalFields?.length > 0 || manualFields?.length > 0 ? (
          <Flex
            direction={"column"}
            className={cx("oc-grid", "oc-grid-narrow", "ocn-list-scrollbar")}
            gap={"16px"}
            overflow={"auto"}
          >
            {additionalFields?.map((fieldName, index) => {
              let fieldDef = FindFieldDef(fieldName);
              if (!fieldDef) return null;

              let fieldChanged =
                GetExistingFieldChanges(fieldName)?.length > 0 || unsavedFieldChange(fieldName);

              const fieldID = fieldDef.GetFieldID();
              const isAuxField = fieldID.indexOf(kAuxSuperID) >= 0;
              if (isAuxField) return null;
              const { enableTE: fieldEnableTE, enableValueSelect: fieldEnableValueSelect } =
                ProcessAdvancedField(
                  fieldDef,
                  changeType ?? workingOverride?.SmartGetFieldValue("Type"),
                  IsOnlyCommentChange(),
                  assignmentStatus
                );
              const finalEnableTE = fieldEnableTE && isEditable;
              const finalEnableValueSelect = fieldEnableValueSelect && isEditable;
              return (
                <FieldInputValuePickerComposite
                  key={index}
                  field={fieldName}
                  value={GetCurrentFieldValue(fieldName)}
                  onChange={UpdateFieldValue}
                  dataTestIdPrefix={dataTestIdPrefix}
                  inputProps={{
                    status: fieldChanged ? "warning" : null,
                    placeholder: "<Blank>",
                    disabled: !finalEnableTE,
                    size: "medium",
                    className: "ocn-plan-explorer-create-plan"
                  }}
                  triggerProps={{ disabled: !finalEnableValueSelect, triggerModifier: "medium" }}
                  showTrigger={isEditable}
                  parentFieldDef={fieldDef}
                  showPickListWarning={true}
                  queryChartId={queryChartId}
                >
                  {isEditable && GetRevertButton(fieldDef)}
                </FieldInputValuePickerComposite>
              );
            })}
            {/* Manual fields from table's additionalFields (no fieldDefs) */}
            {manualFields?.map((fieldName, index) => {
              let fieldChanged =
                GetExistingFieldChanges(fieldName)?.length > 0 || unsavedFieldChange(fieldName);

              const handleManualFieldChange = e => {
                UpdateFieldValue({ field: fieldName, value: e.target.value });
              };

              return (
                <div key={`manual-field-${index}`} className={"oc-field-input-wrapper"}>
                  <Input
                    dataTestId={`${dataTestIdPrefix}${fieldName}`}
                    label={fieldName}
                    placeholder="<Blank>"
                    size="medium"
                    type="text"
                    onChange={handleManualFieldChange}
                    value={GetCurrentFieldValue(fieldName) ?? ""}
                    disabled={!isEditable || changeType == enumOverrideType.delete}
                    status={fieldChanged ? "warning" : null}
                    className="ocn-plan-explorer-create-plan"
                  />
                </div>
              );
            })}
          </Flex>
        ) : additionalFieldsEmptyNode ? (
          <>{additionalFieldsEmptyNode}</>
        ) : (
          <Text size={"small"} colorModifier={"grey"}>
            {appstr("No additional fields added")}
          </Text>
        )}
        {additionalFieldsActionNode}
      </Flex>
    </div>
  );

  // Build Comment Tab
  let commentTab = {};
  commentTab.dataTestId = `${dataTestIdPrefix}tab-comments`;
  commentTab.header = appstr("Comments");
  commentTab.content = (
    <Flex padding={contentPadding} height={"400px"}>
      <div style={{ width: "100%", height: "100%" }}>
        <TextArea
          value={comment ?? ""}
          onChange={UpdateComment}
          placeholder={appstr("Enter comment")}
        />
      </div>
    </Flex>
  );

  // Create tabs array
  let tabs = [basicInfoTab];
  if (props?.showAdditionalReportsTab) {
    tabs.push(additionalReportsTab);
  }
  tabs.push(additionalInfoTab);
  if (showCommentsTab) {
    tabs.push(commentTab);
  }

  const getHeaderTitle = () => {
    const localizedTypes = Object.values(enumOverrideType);
    const headerTypeString = localizedTypes.includes(type) ? appstr(type) : type;
    return headerTypeString + " " + appstr("record");
  };

  // Create header content with record display label
  const recordIDKey = dbObj?.GetRecordIDFieldDef()?.GetFieldID();
  const headerContent = (
    <Flex justifyContent={"space-between"} alignItems={"flex-end"} padding={"0 24px"}>
      <RecordDisplayLabel
        name={recordInfo?.Name}
        id={recordInfo?.[recordIDKey]}
        title={recordInfo?.Title}
        dept={recordInfo?.Division}
        photoSrc={photoSrc}
        gap={"4px"}
      />
      {showEffectiveDate && (
        <div className={"oc-justify-right"}>
          <Flex direction={"column"} gap={"8px"}>
            {isPositionChart && (
              <AssignmentStatusPill
                doc={doc}
                dataObj={dbObj}
                record={record}
                labelProps={{ size: "small" }}
              />
            )}
            <Flex direction="row" alignItems="center" gap="8px">
              <Text size={"small"}>{appstr("Effective date")}</Text>
              <DateInputField
                returnDateObject
                value={isValidDate(effectiveDate) ? effectiveDate.FormatDate(3) : undefined}
                enumDateFormat={Globals._wsMgr.fOrgInfo.GetDateFormat()}
                onChange={(_, nextDate) => {
                  setEffectiveDate(nextDate);
                }}
                data-testid={`${dataTestIdPrefix}effective-date`}
                placeholder={appstr("Enter") + " " + appstr("Effective date")}
                size="small"
              />
            </Flex>
          </Flex>
        </div>
      )}
    </Flex>
  );

  const iconProps = GetChangeTypeIconProps(changeType, { size: "small" });

  return (
    <BaseRecordEditorModal
      show={show}
      overrideObj={overrideObj}
      dbObj={dbObj}
      type={type}
      onSave={handleSaveWithEffectiveDate}
      onClose={onClose}
      manager={manager}
      dataTestIdPrefix={dataTestIdPrefix || "record-editor-"}
      allowOrphans={allowOrphans}
      loadRecord={loadRecord}
      branchAdd={branchAdd}
      initChanges={initChanges}
      skipValidation={skipValidation}
      isPlanEdit={isPlanEdit}
      size={{ width: initialWidth, height: initialHeight }}
      otherHeaderProps={{
        ...otherHeaderProps,
        size: "xlarge",
        labelSize: "large",
        icon: iconProps?.name,
        iconProps: iconProps
      }}
      otherOverlayProps={{ padding: "16px", labelSize: "large" }}
      otherFooterProps={otherFooterProps}
      overlayProps={overlayProps}
      validateFields={validateFields}
      headerContent={headerContent}
      getHeaderTitle={getHeaderTitle}
      onSaveWithChanges={handleSave}
      showComments={false}
      controllerData={controllerData}
      onUnsavedChanges={handleUnsavedChanges}
      onRevertOverride={handleRevertOverride}
      onInitializeTarget={handleInitializeTarget}
      onLoadRecordFallback={handleLoadRecordFallback}
      allowSave={!overrideHasConflicts}
      contentBefore={
        overrideHasConflicts && (
          <div style={{ padding: "20px 20px 0 20px" }}>
            <Alert
              alertActions={{
                customAction: (
                  <Button
                    dataTestId={"plan-conflicts-shortcut"}
                    size={"small"}
                    modifier={"danger"}
                    onClick={() => {
                      setConflictOverride(workingOverride);
                      setShowConflictModal(true);
                    }}
                  >
                    {appstr("Resolve")}
                  </Button>
                )
              }}
              severity="error"
              showIcon
              className={cx("oc-padding-small", "oc-text-small", "oc-data-source-details")}
            >
              {appstr("Conflict record detected")}
            </Alert>
          </div>
        )
      }
    >
      <div className={cx("ocn-chart-explorer-column", "ocn-chart-explorer-tabular")}>
        <Tabs
          tabIndex={parseInt(currentTab)}
          customClass={"ocn-sidebar-tabs"}
          customContentClass={"ocn-chart-explorer-tabs-content"}
          customHeaderClass={"oc-tab-header-no-divider"}
          tabs={tabs}
          tabSize={"medium"}
          onTabChange={handleTabChange}
        />
      </div>
    </BaseRecordEditorModal>
  );
};

export default RecordEditorModal;
